/**
 * The job safety analysis (JSA; R13, MB's decision 837; TJR inventory 01 §6.1; docs/specs/jsa.md): TJR's IAGSA
 * "Airborne Geophysical Survey Risk Assessment" filled in on the field laptop, offline, shared by the project's
 * laptops and Teslyn Cloud (`jsa`, one per project), printed as a PDF with a crew sign-off and a management sign-off,
 * and sent for signing through DocuSeal as the JSP safety meeting is (`./docuseal.ts`, `./jsp-meeting.ts`).
 *
 * Decision 132: the maximum survey time takes away both the flight out and the flight back, and keeps the reserve
 * minutes per aircraft. Decision 853: the signers are the crew on the project on the sign-off date.
 */
import { docusealSignatureTags, SIGNOFF_SIGNERS_MAX } from './docuseal.js';
import {
  jspFileDate,
  jspPrintedDate,
  type SignoffOutboxItem,
  type SignoffRequestRow,
} from './jsp-meeting.js';
import type { Aircraft, Block, Person, PlannedLine, Project, ProjectCrew } from './model.js';
import { REGISTER_ROLE_LABEL } from './staff.js';

export const JSA_MANIFEST_SCHEMA = 'teslyn.jsa.v1';

/** The limits the screen, the laptop's database and Teslyn Cloud all check. */
export const JSA_LIMITS = {
  name: 200,
  field: 100,
  long: 200,
  remarks: 2000,
  additional: 8000,
  blocks: 30,
  aircraft: 10,
  managers: 10,
  signers: SIGNOFF_SIGNERS_MAX,
  drape: 4,
} as const;

// ------------------------------------------------------------------------------------------------ the fields

type Fields<K extends string> = readonly { readonly key: K; readonly label: string }[];

/** The operating conditions, in TJR's print order (`pntjsadc.scp`, record 297). */
export const JSA_OPERATING = [
  { key: 'wind_direction', label: 'Prevailing wind direction' },
  { key: 'wind_speed_kt', label: 'Average wind speed (knots)' },
  { key: 'temp_min_c', label: 'Mean min temp (°C)' },
  { key: 'temp_max_c', label: 'Mean max temp (°C)' },
  { key: 'seasonal_weather', label: 'Seasonal survey weather' },
  { key: 'forecast_sources', label: 'Weather forecast sources' },
  { key: 'elevation_min_ft', label: 'Block elevation minimum (ft)' },
  { key: 'elevation_mean_ft', label: 'Block elevation mean (ft)' },
  { key: 'elevation_max_ft', label: 'Block elevation maximum (ft)' },
  { key: 'season', label: 'Survey operating season' },
  { key: 'daylight', label: 'Sunrise/sunset or daily duration' },
  { key: 'max_daily_flight', label: 'Max daily flight time available' },
  { key: 'emergency_airstrips', label: 'Emergency airstrips available' },
  { key: 'primary_method', label: 'Primary flight following method' },
  { key: 'primary_frequency', label: 'Primary position reporting frequency' },
  { key: 'secondary_method', label: 'Secondary flight following method' },
  { key: 'secondary_frequency', label: 'Secondary position reporting frequency' },
] as const satisfies Fields<string>;
export type JsaOperatingKey = (typeof JSA_OPERATING)[number]['key'];

/** TJR's "Set page defaults" (`ms` 64697–64701). */
export const JSA_OPERATING_DEFAULTS: Partial<Record<JsaOperatingKey, string>> = {
  forecast_sources: 'NAIPS',
  max_daily_flight: '5 hours',
  primary_method: 'Tracplus AFF',
  primary_frequency: '5 minutes',
  emergency_airstrips: 'Nil',
};

export const JSA_GRADIENT = [
  { key: 'flat', label: 'Flat < 60 ft/NM (< 10 m/km)' },
  { key: 'gentle', label: 'Gentle 61–300 ft/NM (11–50 m/km)' },
  { key: 'undulating', label: 'Undulating 301–900 ft/NM (51–150 m/km)' },
  { key: 'steep', label: 'Steep > 900 ft/NM (> 150 m/km)' },
] as const satisfies Fields<string>;
export type JsaGradientKey = (typeof JSA_GRADIENT)[number]['key'];

export const JSA_SURFACE = [
  { key: 'water', label: 'Water' },
  { key: 'desert', label: 'Desert' },
  { key: 'scrub', label: 'Scrub' },
  { key: 'pastoral', label: 'Pastoral' },
  { key: 'wooded', label: 'Wooded' },
  { key: 'jungle', label: 'Jungle' },
] as const satisfies Fields<string>;
export type JsaSurfaceKey = (typeof JSA_SURFACE)[number]['key'];

/** TJR's ten hazards (record 279 field 12, one digit each). */
export const JSA_HAZARDS = [
  { key: 'power_lines', label: 'Power lines' },
  { key: 'towers', label: 'Towers/masts' },
  { key: 'birds', label: 'Known bird activity' },
  { key: 'aircraft', label: 'Known aircraft activity' },
  { key: 'urban', label: 'Urban areas' },
  { key: 'farms', label: 'Farm houses/livestock' },
  { key: 'airstrips', label: 'Airstrips' },
  { key: 'blasting', label: 'Blasting areas' },
  { key: 'restricted', label: 'Restricted/danger areas' },
  { key: 'political', label: 'Politically sensitive areas' },
] as const satisfies Fields<string>;
export type JsaHazardKey = (typeof JSA_HAZARDS)[number]['key'];

/** A hazard's rating; '' not rated. TJR printed "Heavy" for its on-screen "Many" (B16): one word, Many. */
export type JsaRating = '' | 'none' | 'few' | 'moderate' | 'many';
export const JSA_RATINGS = ['none', 'few', 'moderate', 'many'] as const;
export const JSA_RATING_WORDS: Readonly<Record<Exclude<JsaRating, ''>, string>> = {
  none: 'None',
  few: 'Few',
  moderate: 'Moderate',
  many: 'Many',
};

/** The aircraft rows (TJR 012 field 20, in order). */
export const JSA_AIRCRAFT_DETAILS = [
  { key: 'expected_hours', label: 'Expected hours to be flown on survey' },
  { key: 'airframe_hours', label: 'Total airframe time at start of survey' },
  { key: 'engine_left_hours', label: 'Engine hours remaining (L)' },
  { key: 'engine_right_hours', label: 'Engine hours remaining (R)' },
  { key: 'apu_hours', label: 'APU hours remaining' },
  { key: 'camp', label: 'Continuous airworthiness program in place' },
  { key: 'components_extension', label: 'Major components on time extensions' },
  { key: 'components_expiring', label: 'Major components that will expire during survey' },
  { key: 'maintenance', label: 'Projected maintenance during survey' },
  { key: 'pilots_per_flight', label: 'Number of pilots per flight' },
  { key: 'operators_per_flight', label: 'Number of operators on each flight' },
] as const satisfies Fields<string>;
export type JsaAircraftDetailKey = (typeof JSA_AIRCRAFT_DETAILS)[number]['key'];

/** The crew logistics rows (TJR 807 and the crew counts). */
export const JSA_LOGISTICS = [
  { key: 'mechanics', label: 'Number of aircraft mechanics on site' },
  { key: 'engineers', label: 'Number of aircraft engineers on site' },
  { key: 'local_observer', label: 'Local observer or liaison officer on site' },
  { key: 'flight_observer', label: 'Local flight observer on board' },
  { key: 'hangar', label: 'Hangar available on site' },
  { key: 'pilots_on_site', label: 'Number of pilots on site' },
  { key: 'operators_on_site', label: 'Number of operators on site' },
] as const satisfies Fields<string>;
export type JsaLogisticsKey = (typeof JSA_LOGISTICS)[number]['key'];

export const JSA_AIRSPACE = [
  { key: 'civil_controlled', label: 'Civil controlled airspaces' },
  { key: 'prohibited', label: 'Prohibited areas' },
  { key: 'restricted', label: 'Restricted areas' },
  { key: 'danger', label: 'Danger areas' },
  { key: 'military', label: 'Military controlled airspaces' },
  { key: 'alternative_strips', label: 'Alternative landing strips' },
  { key: 'borders', label: 'International borders' },
] as const satisfies Fields<string>;
export const JSA_ENVIRONMENT = [
  { key: 'waste_oil', label: 'Waste oil disposal facilities' },
  { key: 'environment_impact', label: 'Adverse impact on the environment' },
  { key: 'wildlife', label: 'Seasonal wildlife concerns' },
] as const satisfies Fields<string>;
export type JsaAirspaceKey =
  | (typeof JSA_AIRSPACE)[number]['key']
  | (typeof JSA_ENVIRONMENT)[number]['key']
  | 'remarks'
  | 'environment_remarks';

export const JSA_RECON = [
  { key: 'flight_details', label: 'Reconnaissance flight details' },
  { key: 'height_ft', label: 'Flying height for reconnaissance flight (ft)' },
  { key: 'maps_by', label: 'Examination of topographic maps conducted by' },
  { key: 'inform_atc', label: 'Inform ATC of any calibration flights' },
  { key: 'environment_effect', label: 'Reconnaissance flight effect on environment' },
] as const satisfies Fields<string>;
export type JsaReconKey = (typeof JSA_RECON)[number]['key'];

/** The project details the JSA prints that Teslyn does not hold elsewhere (typed; base location from the project). */
export const JSA_DETAILS = [
  { key: 'principal_contact', label: 'Principal contact' },
  { key: 'contact_number', label: 'Contact number' },
  { key: 'base_location', label: 'Base location' },
  { key: 'est_start', label: 'Est start date' },
  { key: 'est_end', label: 'Est end date' },
  { key: 'survey_type', label: 'Survey type' },
] as const satisfies Fields<string>;
export type JsaDetailsKey = (typeof JSA_DETAILS)[number]['key'];

// ------------------------------------------------------------------------------------------------ the record

export interface JsaSigner {
  readonly person_id: string;
  readonly name: string;
  /** The person's role on the project ("Pilot", "Operator"). */
  readonly role: string;
}

export interface JsaManager {
  readonly person_id: string;
  readonly name: string;
  /** As printed under Management sign-off ("Technical", "Operations manager"). */
  readonly position: string;
}

export interface JsaBlock {
  readonly block_id: string;
  readonly name: string;
  /** TJR printed "Contour". */
  readonly ground_mode: string;
  readonly gradient: Readonly<Record<JsaGradientKey, number | null>>;
  readonly surface: Readonly<Record<JsaSurfaceKey, number | null>>;
  readonly tree_height_m: number | null;
  readonly canopy_height_m: number | null;
  readonly hazards: Readonly<Record<JsaHazardKey, JsaRating>>;
  readonly remarks: string;
}

export interface JsaPerformanceInput {
  readonly empty_weight_kg: number | null;
  readonly crew_equipment_kg: number | null;
  readonly planned_fuel_l: number | null;
  readonly mtow_kg: number | null;
  readonly transit_km: number | null;
  readonly cruise_kt: number | null;
  readonly fuel_flow_lph: number | null;
  /** Decision 132: the reserve minutes are kept per aircraft. */
  readonly reserve_min: number | null;
}

export interface JsaAircraft {
  readonly aircraft_id: string;
  readonly registration: string;
  readonly type_name: string;
  readonly details: Readonly<Record<JsaAircraftDetailKey, string>>;
  readonly performance: JsaPerformanceInput;
  readonly fuel_type: '' | 'avgas' | 'turbine';
  readonly verified_person_id: string | null;
  readonly verified_by: string;
  readonly verified_date: string | null;
  readonly remarks: string;
}

export interface JsaRecon extends Readonly<Record<JsaReconKey, string>> {
  readonly remarks: string;
  readonly drape: readonly { readonly range: string; readonly percent: string }[];
  readonly drape_remarks: string;
}

/** The JSA as the screen edits it. */
export interface JsaDraft {
  readonly part_a_person_id: string | null;
  readonly part_a_name: string;
  readonly part_a_date: string | null;
  readonly part_b_person_id: string | null;
  readonly part_b_name: string;
  readonly part_b_date: string | null;
  readonly details: Readonly<Record<JsaDetailsKey, string>>;
  readonly operating: Readonly<Record<JsaOperatingKey | 'remarks', string>>;
  readonly blocks: readonly JsaBlock[];
  readonly aircraft: readonly JsaAircraft[];
  readonly logistics: Readonly<Record<JsaLogisticsKey, string>>;
  readonly airspace: Readonly<Record<JsaAirspaceKey, string>>;
  readonly recon: JsaRecon;
  readonly additional_risks: string;
  readonly sign_date: string;
  readonly crew: readonly JsaSigner[];
  readonly managers: readonly JsaManager[];
}

/** A `jsa` row as Teslyn Field reads it. */
export interface Jsa extends JsaDraft {
  readonly id: string;
  readonly project_id: string;
  /** Which pictures were taken or chosen, and when (`location`, `base`, `drape`, `block-<block id>`). */
  readonly pictures: Readonly<Record<string, string>>;
  readonly pdf_name: string;
  readonly pdf_sha256: string;
  readonly pdf_revision: number;
  readonly pdf_made_at: string | null;
  readonly recorded_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

/** Who may write the JSA: as the JSP meeting (TJR's JSA was filled in Job Field Setup on the field laptop). */
export const JSA_RECORD_ROLES = ['tenant_admin', 'manager', 'field_crew', 'data_processor'] as const;

/** Whether one of `roles` writes the JSA. A laptop not linked to an organisation yet (no roles) does too. */
export function canRecordJsa(roles: readonly string[] | null): boolean {
  if (roles === null) return true;
  return roles.some((r) => (JSA_RECORD_ROLES as readonly string[]).includes(r));
}

// ------------------------------------------------------------------------------------------------ defaults

const blank = <K extends string>(fields: Fields<K>): Record<K, string> =>
  Object.fromEntries(fields.map((f) => [f.key, ''])) as Record<K, string>;
const nulls = <K extends string>(fields: Fields<K>): Record<K, number | null> =>
  Object.fromEntries(fields.map((f) => [f.key, null])) as Record<K, number | null>;

/** A block's empty terrain and hazards row. */
export function jsaBlockRow(block: Pick<Block, 'id' | 'name'>): JsaBlock {
  return {
    block_id: block.id,
    name: block.name,
    ground_mode: 'Contour',
    gradient: nulls(JSA_GRADIENT),
    surface: nulls(JSA_SURFACE),
    tree_height_m: null,
    canopy_height_m: null,
    hazards: Object.fromEntries(JSA_HAZARDS.map((h) => [h.key, ''])) as Record<JsaHazardKey, JsaRating>,
    remarks: '',
  };
}

const num = (s: unknown): number | null => {
  if (typeof s === 'number') return Number.isFinite(s) ? s : null;
  if (typeof s !== 'string' || !s.trim()) return null;
  const n = Number(s.trim());
  return Number.isFinite(n) ? n : null;
};

/**
 * An aircraft's row, with what the aircraft register already knows: fuel flow, fuel type, TJR's cruise speed, and the
 * empty weight, MTOW and reserve minutes kept in the register (decision 863, 6 B; aircraft-performance.ts).
 */
export function jsaAircraftRow(
  a: Pick<Aircraft, 'id' | 'registration' | 'type_name' | 'fuel_flow_lph' | 'legacy_json'> &
    Partial<Pick<Aircraft, 'fuel_type' | 'empty_weight_kg' | 'mtow_kg' | 'reserve_min'>>,
): JsaAircraft {
  const legacy = (a.legacy_json ?? {}) as Record<string, unknown>;
  return {
    aircraft_id: a.id,
    registration: a.registration,
    type_name: a.type_name,
    details: blank(JSA_AIRCRAFT_DETAILS),
    performance: {
      empty_weight_kg: a.empty_weight_kg ?? null,
      crew_equipment_kg: null,
      planned_fuel_l: null,
      mtow_kg: a.mtow_kg ?? null,
      transit_km: null,
      cruise_kt: num(legacy['cruise_kts']),
      fuel_flow_lph: a.fuel_flow_lph ?? null,
      reserve_min: a.reserve_min ?? null,
    },
    fuel_type: a.fuel_type ?? '',
    verified_person_id: null,
    verified_by: '',
    verified_date: null,
    remarks: '',
  };
}

/** The live crew rows of the project on `date` (open dates count), as signers with their project role (decision 853). */
export function projectCrewOn(
  date: string,
  crew: readonly ProjectCrew[],
  people: readonly Person[],
): JsaSigner[] {
  const out: JsaSigner[] = [];
  for (const c of crew) {
    if (c.deleted_at || !c.person_id) continue;
    if ((c.start_date && c.start_date > date) || (c.end_date && c.end_date < date)) continue;
    const p = people.find((x) => x.id === c.person_id && !x.deleted_at);
    if (!p || out.some((a) => a.person_id === p.id)) continue;
    out.push({ person_id: p.id, name: p.name, role: REGISTER_ROLE_LABEL[c.role] });
  }
  return out;
}

/** The crew logistics counts TJR filled from the job's pilots and operators. */
function crewCounts(
  crew: readonly JsaSigner[],
): Pick<Record<JsaLogisticsKey, string>, 'pilots_on_site' | 'operators_on_site'> {
  const n = (role: string): string => String(crew.filter((c) => c.role === role).length);
  return { pilots_on_site: n(REGISTER_ROLE_LABEL.pilot), operators_on_site: n(REGISTER_ROLE_LABEL.operator) };
}

/** A new JSA: the project's survey blocks and aircraft, its base, today's crew (decision 853). */
export function newJsa(input: {
  readonly project: Pick<Project, 'operating_base'>;
  readonly blocks: readonly Pick<Block, 'id' | 'name' | 'kind' | 'deleted_at'>[];
  readonly aircraft: readonly (Pick<
    Aircraft,
    'id' | 'registration' | 'type_name' | 'fuel_flow_lph' | 'legacy_json' | 'deleted_at'
  > &
    Partial<Pick<Aircraft, 'fuel_type' | 'empty_weight_kg' | 'mtow_kg' | 'reserve_min'>>)[];
  readonly crew: readonly JsaSigner[];
  readonly today: string;
}): JsaDraft {
  return {
    part_a_person_id: null,
    part_a_name: '',
    part_a_date: null,
    part_b_person_id: null,
    part_b_name: '',
    part_b_date: null,
    details: { ...blank(JSA_DETAILS), base_location: input.project.operating_base ?? '' },
    operating: { ...blank(JSA_OPERATING), remarks: '' },
    blocks: input.blocks.filter((b) => !b.deleted_at && b.kind === 'survey').map(jsaBlockRow),
    aircraft: input.aircraft.filter((a) => !a.deleted_at).map(jsaAircraftRow),
    logistics: { ...blank(JSA_LOGISTICS), ...crewCounts(input.crew) },
    airspace: {
      ...blank(JSA_AIRSPACE),
      ...blank(JSA_ENVIRONMENT),
      remarks: '',
      environment_remarks: '',
    },
    recon: {
      ...blank(JSA_RECON),
      remarks: '',
      drape: Array.from({ length: JSA_LIMITS.drape }, () => ({ range: '', percent: '' })),
      drape_remarks: '',
    },
    additional_risks: '',
    sign_date: input.today,
    crew: [...input.crew],
    managers: [],
  };
}

/**
 * Rows for survey blocks added to the project after the JSA was started (none is removed: it was printed). Aircraft are
 * added and removed by hand ("Add an aircraft"): an organisation's register holds more aircraft than one survey flies.
 */
export function jsaWithProject(
  d: JsaDraft,
  blocks: readonly Pick<Block, 'id' | 'name' | 'kind' | 'deleted_at'>[],
): JsaDraft {
  const newBlocks = blocks
    .filter((b) => !b.deleted_at && b.kind === 'survey' && !d.blocks.some((x) => x.block_id === b.id))
    .map(jsaBlockRow);
  return newBlocks.length === 0 ? d : { ...d, blocks: [...d.blocks, ...newBlocks] };
}

/** The aircraft a new JSA starts with: those that flew in the project, else every aircraft of the register. */
export function jsaStartAircraft<A extends Pick<Aircraft, 'id' | 'deleted_at'>>(
  aircraft: readonly A[],
  flights: readonly { readonly aircraft_id: string | null; readonly deleted_at?: string | null }[],
): A[] {
  const live = aircraft.filter((a) => !a.deleted_at);
  const flown = live.filter((a) => flights.some((f) => !f.deleted_at && f.aircraft_id === a.id));
  return flown.length > 0 ? flown : live;
}

// ------------------------------------------------------------------------------------------------ performance

/** Fuel density, kg per litre: TJR's 0.80 for turbine fuel; avgas 0.72. */
export function jsaFuelDensity(fuelType: JsaAircraft['fuel_type']): number {
  return fuelType === 'avgas' ? 0.72 : 0.8;
}

/** TJR's take-off and landing allowance, minutes. */
export const JSA_TAKEOFF_LANDING_MIN = 10;

export interface JsaPerformance {
  readonly a_kg: number | null;
  readonly b_kg: number | null;
  readonly c_kg: number | null;
  readonly d_l: number | null;
  readonly d_kg: number | null;
  readonly e_kg: number | null;
  readonly f_kg: number | null;
  readonly g_km: number | null;
  readonly g_l: number | null;
  readonly h_l: number | null;
  readonly i_l: number | null;
  /** The fuel left for surveying: D − 2 × G − H − I (decision 132). */
  readonly survey_fuel_l: number | null;
  /** J, hours. */
  readonly j_h: number | null;
  readonly density: number;
  /** E is more than F. */
  readonly over_mtow: boolean;
}

/**
 * TJR's performance planning A to J (`fs` §10), with decision 132: J = (D − 2 × G − H − I) ÷ fuel flow, the flight
 * out and the flight back both taken off. A figure is null while what it needs is not typed.
 */
export function jsaPerformance(a: Pick<JsaAircraft, 'performance' | 'fuel_type'>): JsaPerformance {
  const p = a.performance;
  const density = jsaFuelDensity(a.fuel_type);
  const add = (x: number | null, y: number | null): number | null =>
    x === null || y === null ? null : x + y;
  const flow = p.fuel_flow_lph !== null && p.fuel_flow_lph > 0 ? p.fuel_flow_lph : null;
  const c = add(p.empty_weight_kg, p.crew_equipment_kg);
  const dKg = p.planned_fuel_l === null ? null : p.planned_fuel_l * density;
  const e = add(c, dKg);
  const g =
    p.transit_km === null || flow === null || p.cruise_kt === null || !(p.cruise_kt > 0)
      ? null
      : (p.transit_km / (p.cruise_kt * 1.852)) * flow;
  const h = p.reserve_min === null || flow === null ? null : (p.reserve_min / 60) * flow;
  const i = flow === null ? null : (JSA_TAKEOFF_LANDING_MIN / 60) * flow;
  const fuel =
    p.planned_fuel_l === null || g === null || h === null || i === null
      ? null
      : p.planned_fuel_l - 2 * g - h - i;
  const j = fuel === null || flow === null ? null : Math.max(0, fuel) / flow;
  return {
    a_kg: p.empty_weight_kg,
    b_kg: p.crew_equipment_kg,
    c_kg: c,
    d_l: p.planned_fuel_l,
    d_kg: dKg,
    e_kg: e,
    f_kg: p.mtow_kg,
    g_km: p.transit_km,
    g_l: g,
    h_l: h,
    i_l: i,
    survey_fuel_l: fuel,
    j_h: j,
    density,
    over_mtow: e !== null && p.mtow_kg !== null && e > p.mtow_kg,
  };
}

const one = (n: number | null): string => (n === null ? '' : n.toFixed(1));

/** The printed J line: "980.2 L fuel at 217 L/h = 4.5 h (the flight out and the flight back taken off)". */
export function jsaSurveyTimeWords(perf: JsaPerformance, flowLph: number | null): string {
  if (perf.j_h === null || perf.survey_fuel_l === null || flowLph === null) return '';
  return `${one(perf.survey_fuel_l)} L fuel at ${String(flowLph)} L/h = ${one(perf.j_h)} h (the flight out and the flight back taken off)`;
}

// ------------------------------------------------------------------------------------------------ checks

const DATE = /^\d{4}-\d{2}-\d{2}$/;
function validDate(s: string | null): boolean {
  if (!s || !DATE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

const pct = (
  r: Readonly<Record<string, number | null>>,
): { readonly any: boolean; readonly total: number } => {
  const v = Object.values(r);
  return { any: v.some((x) => x !== null), total: v.reduce<number>((s, x) => s + (x ?? 0), 0) };
};

function tooLong(out: string[], what: string, s: string, max: number): void {
  if (s.trim().length > max) out.push(`Keep ${what} to ${max} characters.`);
}

/**
 * What stops a JSA being saved, in words (empty when it can be). `surveyHeights` is each block's survey height in
 * metres (Project settings, QC limits; decision 822), for TJR's "Tree and or Canopy height is same or greater than
 * Terrain height".
 */
export function checkJsa(d: JsaDraft, surveyHeights: Readonly<Record<string, number | null>> = {}): string[] {
  const out: string[] = [];
  if (!validDate(d.sign_date)) out.push('Choose the sign-off date.');
  for (const [part, date, name] of [
    ['Part A', d.part_a_date, d.part_a_name],
    ['Part B', d.part_b_date, d.part_b_name],
  ] as const) {
    if (date !== null && !validDate(date)) out.push(`${part}: the date is not a date.`);
    tooLong(out, `the name of who completed ${part}`, name, JSA_LIMITS.name);
  }
  for (const f of JSA_DETAILS) tooLong(out, f.label.toLowerCase(), d.details[f.key] ?? '', JSA_LIMITS.long);
  for (const f of JSA_OPERATING)
    tooLong(out, f.label.toLowerCase(), d.operating[f.key] ?? '', JSA_LIMITS.field);
  tooLong(out, 'the operating conditions remarks', d.operating.remarks ?? '', JSA_LIMITS.remarks);
  if (d.blocks.length > JSA_LIMITS.blocks) out.push(`A JSA has at most ${JSA_LIMITS.blocks} blocks.`);
  for (const b of d.blocks) {
    for (const [what, row] of [
      ['terrain gradient', b.gradient],
      ['surface', b.surface],
    ] as const) {
      const vals = Object.values(row as Record<string, number | null>);
      if (vals.some((x) => x !== null && (!(x >= 0) || x > 100)))
        out.push(`${b.name}: each ${what} figure is 0 to 100 %.`);
      const t = pct(row);
      if (t.any && Math.abs(t.total - 100) > 0.001)
        out.push(`${b.name}: the ${what} adds up to ${t.total} %, not 100 %.`);
    }
    for (const [what, h] of [
      ['tree height', b.tree_height_m],
      ['canopy height', b.canopy_height_m],
    ] as const)
      if (h !== null && !(h >= 0)) out.push(`${b.name}: the ${what} is 0 m or more.`);
    const sh = surveyHeights[b.block_id] ?? null;
    if (sh !== null && ((b.tree_height_m ?? 0) >= sh || (b.canopy_height_m ?? 0) >= sh))
      out.push(`${b.name}: the tree and canopy heights must be below the survey height (${sh} m).`);
    tooLong(out, `${b.name}'s other hazards`, b.remarks, JSA_LIMITS.remarks);
    tooLong(out, `${b.name}'s survey ground mode`, b.ground_mode, JSA_LIMITS.field);
  }
  if (d.aircraft.length > JSA_LIMITS.aircraft) out.push(`A JSA has at most ${JSA_LIMITS.aircraft} aircraft.`);
  for (const a of d.aircraft) {
    const p = a.performance;
    for (const [k, v] of Object.entries(p) as [keyof JsaPerformanceInput, number | null][])
      if (v !== null && !(v >= 0)) out.push(`${a.registration}: ${PERFORMANCE_WORDS[k]} is 0 or more.`);
    if (p.fuel_flow_lph !== null && !(p.fuel_flow_lph > 0))
      out.push(`${a.registration}: the fuel flow is more than 0 L/h.`);
    if (p.cruise_kt !== null && !(p.cruise_kt > 0))
      out.push(`${a.registration}: the cruise speed is more than 0 kt.`);
    if (p.reserve_min !== null && p.reserve_min > 240)
      out.push(`${a.registration}: the reserve is at most 240 minutes.`);
    if (a.verified_date !== null && !validDate(a.verified_date))
      out.push(`${a.registration}: the performance verified date is not a date.`);
    for (const f of JSA_AIRCRAFT_DETAILS)
      tooLong(out, `${a.registration}'s ${f.label.toLowerCase()}`, a.details[f.key] ?? '', JSA_LIMITS.field);
    tooLong(out, `${a.registration}'s remarks`, a.remarks, JSA_LIMITS.remarks);
  }
  for (const f of JSA_LOGISTICS)
    tooLong(out, f.label.toLowerCase(), d.logistics[f.key] ?? '', JSA_LIMITS.field);
  for (const f of [...JSA_AIRSPACE, ...JSA_ENVIRONMENT])
    tooLong(out, f.label.toLowerCase(), d.airspace[f.key] ?? '', JSA_LIMITS.long);
  tooLong(out, 'the air space remarks', d.airspace.remarks ?? '', JSA_LIMITS.remarks);
  tooLong(out, 'the environment remarks', d.airspace.environment_remarks ?? '', JSA_LIMITS.remarks);
  for (const f of JSA_RECON) tooLong(out, f.label.toLowerCase(), d.recon[f.key] ?? '', JSA_LIMITS.long);
  tooLong(out, 'the reconnaissance remarks', d.recon.remarks, JSA_LIMITS.remarks);
  tooLong(out, 'the drape analysis remarks', d.recon.drape_remarks, JSA_LIMITS.remarks);
  if (d.recon.drape.length > JSA_LIMITS.drape) out.push(`The drape analysis has ${JSA_LIMITS.drape} rows.`);
  tooLong(out, 'the additional risk management items', d.additional_risks, JSA_LIMITS.additional);
  // the signers
  const seen = new Set<string>();
  for (const s of [...d.crew, ...d.managers]) {
    if (seen.has(s.person_id)) out.push(`${s.name} is listed twice to sign.`);
    seen.add(s.person_id);
  }
  if (d.managers.length > JSA_LIMITS.managers)
    out.push(`The management sign-off has at most ${JSA_LIMITS.managers} people.`);
  if (d.crew.length + d.managers.length > JSA_LIMITS.signers)
    out.push(`A JSA has at most ${JSA_LIMITS.signers} signers.`);
  for (const m of d.managers) tooLong(out, `${m.name}'s position`, m.position, JSA_LIMITS.field);
  return [...new Set(out)];
}

const PERFORMANCE_WORDS: Readonly<Record<keyof JsaPerformanceInput, string>> = {
  empty_weight_kg: 'the empty weight',
  crew_equipment_kg: 'the crew and equipment weight',
  planned_fuel_l: 'the planned fuel',
  mtow_kg: 'the MTOW',
  transit_km: 'the transit',
  cruise_kt: 'the cruise speed',
  fuel_flow_lph: 'the fuel flow',
  reserve_min: 'the reserve',
};

/**
 * What TJR checked before printing (`dc:FUN_006c99d4`, `dc:FUN_006cb820`), in words: empty when the PDF can be made.
 */
export function jsaMissing(d: JsaDraft): string[] {
  const out: string[] = [];
  if (!d.part_a_name.trim() || !validDate(d.part_a_date))
    out.push('Choose who completed Part A (survey safety information) and when.');
  if (!d.part_b_name.trim() || !validDate(d.part_b_date))
    out.push('Choose who completed Part B (risk assessment) and when.');
  const op = JSA_OPERATING.filter((f) => !(d.operating[f.key] ?? '').trim()).map((f) => f.label);
  if (op.length) out.push(`Fill in the operating conditions: ${op.join(', ')}.`);
  for (const b of d.blocks) {
    const need: string[] = [];
    if (Math.abs(pct(b.gradient).total - 100) > 0.001) need.push('the terrain gradient (100 % in all)');
    if (Math.abs(pct(b.surface).total - 100) > 0.001) need.push('the surface (100 % in all)');
    if (b.tree_height_m === null) need.push('the tree height');
    if (b.canopy_height_m === null) need.push('the canopy height');
    if (need.length) out.push(`${b.name}: fill in ${need.join(', ')}.`);
  }
  for (const a of d.aircraft) {
    const p = a.performance;
    const need = (
      [
        ['empty_weight_kg', 'A empty weight'],
        ['crew_equipment_kg', 'B crew and equipment'],
        ['planned_fuel_l', 'D planned fuel'],
        ['mtow_kg', 'F MTOW'],
        ['transit_km', 'G transit'],
        ['cruise_kt', 'cruise speed'],
        ['fuel_flow_lph', 'fuel flow'],
        ['reserve_min', 'reserve minutes'],
      ] as const
    )
      .filter(([k]) => p[k] === null)
      .map(([, w]): string => w);
    if (!a.verified_by.trim() || !validDate(a.verified_date))
      need.push('who verified the performance and when');
    if (need.length) out.push(`${a.registration}: fill in ${need.join(', ')}.`);
  }
  if (d.crew.length === 0) out.push('Tick the crew who sign.');
  return out;
}

// ------------------------------------------------------------------------------------------------ from the TJR job

const tjrDate = (s: string | undefined): string | null => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((s ?? '').trim());
  return m ? `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}` : null;
};
const lines = (s: string | undefined): string =>
  (s ?? '')
    .split('|')
    .map((x) => x.trim())
    .filter(Boolean)
    .join('\n');
const RATING_DIGIT: Readonly<Record<string, JsaRating>> = {
  '1': 'none',
  '2': 'few',
  '3': 'moderate',
  '4': 'many',
};

/** The TJR records of an imported job (`project.legacy_json.unmodelled`, `[code, raw]`). */
function tjrRecords(legacy: Readonly<Record<string, unknown>>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const raw = legacy['unmodelled'];
  if (!Array.isArray(raw)) return out;
  for (const r of raw) {
    if (!Array.isArray(r) || typeof r[0] !== 'string' || typeof r[1] !== 'string') continue;
    const list = out.get(r[0]) ?? [];
    list.push(r[1]);
    out.set(r[0], list);
  }
  return out;
}

/** Whether the project came from a TJR job that has JSA records ("Fill from the TJR job"). */
export function jsaHasTjr(project: Pick<Project, 'legacy_json'>): boolean {
  const r = tjrRecords(project.legacy_json ?? {});
  return r.has('297') || r.has('279') || r.has('810') || r.has('814');
}

/**
 * Fill a JSA from TJR's records of an imported job (spec §2.2): 297 operating conditions, 279 terrain and hazards
 * (one per block, in block order), 802 and 803 the authors' dates, 807 the logistics, 808 the verification date,
 * 810 to 817 air space, environment, reconnaissance and drape, and each aircraft's 012 field 20 (`jsa_data`). Fields
 * TJR left empty keep what the draft has.
 */
export function jsaFromTjr(
  d: JsaDraft,
  project: Pick<Project, 'legacy_json'>,
  aircraft: readonly Pick<Aircraft, 'id' | 'legacy_json'>[],
): JsaDraft {
  const rec = tjrRecords(project.legacy_json ?? {});
  const first = (code: string): string[] | null => {
    const r = rec.get(code)?.[0];
    return r === undefined ? null : r.split('~');
  };
  const keep = (was: string, v: string | undefined): string => (v !== undefined && v.trim() ? v.trim() : was);

  let operating = d.operating;
  const f297 = first('297');
  if (f297) {
    const at: Record<JsaOperatingKey, number> = {
      wind_direction: 0,
      wind_speed_kt: 1,
      temp_min_c: 2,
      temp_max_c: 3,
      seasonal_weather: 4,
      forecast_sources: 5,
      elevation_min_ft: 6,
      elevation_mean_ft: 7,
      elevation_max_ft: 8,
      season: 9,
      secondary_frequency: 10,
      daylight: 11,
      max_daily_flight: 12,
      secondary_method: 13,
      emergency_airstrips: 14,
      primary_method: 15,
      primary_frequency: 16,
    };
    const next: Record<string, string> = { ...d.operating };
    for (const [k, i] of Object.entries(at)) next[k] = keep(next[k] ?? '', f297[i]);
    next['remarks'] = keep(next['remarks'] ?? '', lines(f297[17]));
    operating = next as JsaDraft['operating'];
  }

  const terrain = rec.get('279') ?? [];
  const blocks = d.blocks.map((b, i) => {
    const raw = terrain[i];
    if (raw === undefined) return b;
    const f = raw.split('~');
    const n = (j: number): number | null => num(f[j]);
    const digits = (f[12] ?? '').trim();
    const hazards = Object.fromEntries(
      JSA_HAZARDS.map((h, j) => [h.key, RATING_DIGIT[digits[j] ?? ''] ?? b.hazards[h.key]]),
    ) as Record<JsaHazardKey, JsaRating>;
    return {
      ...b,
      gradient: { flat: n(0), gentle: n(1), undulating: n(2), steep: n(3) },
      surface: { water: n(4), desert: n(5), scrub: n(6), pastoral: n(7), wooded: n(8), jungle: n(9) },
      tree_height_m: n(10),
      canopy_height_m: n(11),
      hazards,
      remarks: keep(b.remarks, lines(f[13])),
    };
  });

  const f807 = first('807');
  const logistics = f807
    ? {
        ...d.logistics,
        mechanics: keep(d.logistics.mechanics, f807[0]),
        engineers: keep(d.logistics.engineers, f807[1]),
        local_observer: keep(d.logistics.local_observer, f807[2]),
        flight_observer: keep(d.logistics.flight_observer, f807[3]),
        hangar: keep(d.logistics.hangar, f807[4]),
      }
    : d.logistics;

  const verified = tjrDate(first('808')?.[0]);
  const DETAIL_ORDER = JSA_AIRCRAFT_DETAILS.map((x) => x.key);
  const air = d.aircraft.map((a) => {
    const src = aircraft.find((x) => x.id === a.aircraft_id);
    const raw = (src?.legacy_json as Record<string, unknown> | undefined)?.['jsa_data'];
    const f = typeof raw === 'string' && raw ? raw.split('^') : null;
    const details = { ...a.details } as Record<JsaAircraftDetailKey, string>;
    if (f) DETAIL_ORDER.forEach((k, i) => (details[k] = keep(details[k], f[i])));
    const p = a.performance;
    return {
      ...a,
      details,
      performance: f
        ? {
            ...p,
            crew_equipment_kg: num(f[12]) ?? p.crew_equipment_kg,
            planned_fuel_l: num(f[13]) ?? p.planned_fuel_l,
            transit_km: num(f[14]) ?? p.transit_km,
          }
        : p,
      remarks: f ? keep(a.remarks, lines(f[11])) : a.remarks,
      verified_date: a.verified_date ?? verified,
    };
  });

  const f810 = first('810');
  const f811 = first('811');
  let airspace = d.airspace;
  if (f810 || f811 || rec.has('812') || rec.has('813')) {
    const next: Record<string, string> = { ...d.airspace };
    JSA_AIRSPACE.forEach((x, i) => (next[x.key] = keep(next[x.key] ?? '', f810?.[i])));
    JSA_ENVIRONMENT.forEach((x, i) => (next[x.key] = keep(next[x.key] ?? '', f811?.[i])));
    next['remarks'] = keep(next['remarks'] ?? '', lines(rec.get('812')?.[0]));
    next['environment_remarks'] = keep(next['environment_remarks'] ?? '', lines(rec.get('813')?.[0]));
    airspace = next as JsaDraft['airspace'];
  }

  const f814 = first('814');
  const f816 = first('816');
  const recon: JsaRecon = {
    ...d.recon,
    ...Object.fromEntries(JSA_RECON.map((x, i) => [x.key, keep(d.recon[x.key], f814?.[i])])),
    remarks: keep(d.recon.remarks, lines(rec.get('815')?.[0])),
    drape: f816
      ? Array.from({ length: JSA_LIMITS.drape }, (_, i) => ({
          range: keep(d.recon.drape[i]?.range ?? '', f816[i * 2]),
          percent: keep(d.recon.drape[i]?.percent ?? '', f816[i * 2 + 1]),
        }))
      : d.recon.drape,
    drape_remarks: keep(d.recon.drape_remarks, lines(rec.get('817')?.[0])),
  };

  return {
    ...d,
    part_a_date: d.part_a_date ?? tjrDate(first('802')?.[0]),
    part_b_date: d.part_b_date ?? tjrDate(first('803')?.[0]),
    operating,
    blocks,
    aircraft: air,
    logistics,
    airspace,
    recon,
  };
}

// ------------------------------------------------------------------------------------------------ files and signers

/**
 * The PDF's file name in `Documents\Job Specific Documents\`: `<project folder>_JSA_<d-Month-yyyy>.pdf` with the
 * JSA's sign-off date (`sign_date`), as the daily reports and the JSP meeting are named; `_Signed` names the signed
 * copy. Never a revision number (R139, MB 2 Nov 2026): the database keeps `pdf_revision`, the file carries the date.
 * A PDF made again on the same date takes the earlier file's place (decision 30).
 */
export function jsaPdfFileName(folderName: string, signDate: string, signed = false): string {
  const base = folderName.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'Project';
  return `${base}_JSA_${jspFileDate(signDate)}${signed ? '_Signed' : ''}.pdf`;
}

/** The people DocuSeal asks, in the PDF's order: the crew, then the management sign-off. */
export function jsaSigners(d: Pick<JsaDraft, 'crew' | 'managers'>): { readonly person_id: string }[] {
  return [...d.crew, ...d.managers].map((s) => ({ person_id: s.person_id }));
}

/** The picture keys a JSA can hold, with their captions (spec §3). */
export function jsaPictureSlots(d: Pick<JsaDraft, 'blocks'>): readonly {
  readonly key: string;
  readonly caption: string;
  readonly fromMap: boolean;
  readonly fromFile: boolean;
}[] {
  return [
    { key: 'location', caption: 'Survey location map', fromMap: true, fromFile: true },
    ...d.blocks.map((b) => ({
      key: `block-${b.block_id}`,
      caption: `Block: ${b.name}`,
      fromMap: true,
      fromFile: true,
    })),
    { key: 'base', caption: 'Airport and survey base', fromMap: true, fromFile: true },
    { key: 'drape', caption: 'Drape analysis map', fromMap: true, fromFile: true },
  ];
}

/** Whether a picture key is one Teslyn makes (`location`, `base`, `drape`, `block-<uuid-ish>`). */
export function jsaPictureKeyOk(key: string): boolean {
  return /^(location|base|drape|block-[0-9A-Za-z-]{1,64})$/.test(key);
}

/** The newest sign-off request of a document (any revision), and whether it is of the document's current PDF. */
export function documentSignoffOf(
  kind: string,
  doc: { readonly id: string; readonly pdf_revision: number },
  requests: readonly SignoffRequestRow[],
): { readonly request: SignoffRequestRow | null; readonly current: boolean } {
  const mine = requests
    .filter((r) => r.document_kind === kind && r.document_id === doc.id)
    .sort((a, b) => b.document_revision - a.document_revision || b.created_at.localeCompare(a.created_at));
  const request = mine[0] ?? null;
  return { request, current: !!request && request.document_revision === doc.pdf_revision };
}

/** Whether the JSA changed after its PDF was made (or has none). */
export function jsaNeedsNewPdf(j: Pick<Jsa, 'pdf_made_at' | 'updated_at' | 'pdf_name'>): boolean {
  if (!j.pdf_made_at || !j.pdf_name) return true;
  return Date.parse(j.updated_at) > Date.parse(j.pdf_made_at) + 1000;
}

/** Why "Send for sign-off" is not offered yet, in words, or null when it can be sent. */
export function jsaSendBlocked(
  j: Pick<Jsa, 'pdf_made_at' | 'updated_at' | 'pdf_name' | 'pdf_revision' | 'crew' | 'managers'>,
  signoff: { readonly request: SignoffRequestRow | null; readonly current: boolean },
  queued: SignoffOutboxItem | null,
): string | null {
  if (jsaNeedsNewPdf(j)) return 'Make the PDF first: the JSA changed since it was made.';
  if (j.crew.length === 0) return 'Tick the crew who sign.';
  if (queued && queued.state === 'queued' && queued.document_revision === j.pdf_revision)
    return 'Already queued: it is sent for sign-off when Teslyn Field is online and signed in.';
  const r = signoff.current ? signoff.request : null;
  if (r && (r.state === 'sending' || r.state === 'sent')) return 'Already sent for sign-off.';
  if (r && r.state === 'completed') return 'Signed by everyone.';
  return null;
}

// ------------------------------------------------------------------------------------------------ survey specifications

export interface JsaBlockSpec {
  readonly name: string;
  readonly survey_height_m: number | null;
  readonly production: {
    readonly count: number;
    readonly km: number;
    readonly direction: number | null;
    readonly spacing: number | null;
  };
  readonly tie: {
    readonly count: number;
    readonly km: number;
    readonly direction: number | null;
    readonly spacing: number | null;
  };
}

/** A block's survey specifications from the plan (original lines only; reflights and patches are not the plan). */
export function jsaBlockSpec(
  block: Pick<
    Block,
    | 'id'
    | 'name'
    | 'terrain_clearance_m'
    | 'trav_spacing_m'
    | 'trav_direction_deg'
    | 'tie_spacing_m'
    | 'tie_direction_deg'
  >,
  lines: readonly Pick<
    PlannedLine,
    'block_id' | 'kind' | 'length_m' | 'reflight_of_line_id' | 'deleted_at'
  >[],
): JsaBlockSpec {
  const mine = lines.filter((l) => l.block_id === block.id && !l.deleted_at && !l.reflight_of_line_id);
  const of = (kind: 'traverse' | 'tie'): { count: number; km: number } => {
    const ls = mine.filter((l) => l.kind === kind);
    return { count: ls.length, km: ls.reduce((s, l) => s + l.length_m, 0) / 1000 };
  };
  return {
    name: block.name,
    survey_height_m: block.terrain_clearance_m,
    production: { ...of('traverse'), direction: block.trav_direction_deg, spacing: block.trav_spacing_m },
    tie: { ...of('tie'), direction: block.tie_direction_deg, spacing: block.tie_spacing_m },
  };
}

// ------------------------------------------------------------------------------------------------ the PDF manifest

export interface JsaManifestTenant {
  readonly key: string;
  readonly name: string;
  readonly brand: string;
}

type Row = readonly [string, string];

export interface JsaManifestSigner {
  readonly name: string;
  /** The role (crew) or the position (managers). */
  readonly role: string;
  readonly signature_tag: string;
  readonly date_tag: string;
}

/** The manifest `report::jsa` renders (mirrored with serde in `crates/teslyn-core/src/report/jsa.rs`). */
export interface JsaManifest {
  readonly schema: typeof JSA_MANIFEST_SCHEMA;
  readonly tenant: JsaManifestTenant;
  readonly job_number: string;
  readonly client: string;
  readonly project_title: string;
  readonly revision: string;
  readonly authors: readonly { readonly section: string; readonly name: string; readonly date: string }[];
  readonly crew: readonly JsaManifestSigner[];
  readonly managers: readonly JsaManifestSigner[];
  readonly details: readonly Row[];
  readonly blocks: readonly {
    readonly title: string;
    readonly spec: readonly Row[];
    readonly picture: string;
    readonly picture_caption: string;
  }[];
  readonly operating: readonly Row[];
  readonly operating_remarks: string;
  readonly terrain: readonly {
    readonly title: string;
    readonly gradient: readonly Row[];
    readonly surface: readonly Row[];
    readonly heights: readonly Row[];
    readonly hazards: readonly { readonly label: string; readonly rating: string }[];
    readonly remarks: string;
  }[];
  readonly aircraft: readonly {
    readonly title: string;
    readonly rows: readonly Row[];
    readonly remarks: string;
  }[];
  readonly logistics: readonly Row[];
  readonly performance: readonly {
    readonly title: string;
    readonly rows: readonly (readonly [string, string, string])[];
    readonly survey_time: string;
    readonly over_mtow: boolean;
    readonly verified_by: string;
    readonly verified_date: string;
  }[];
  readonly airspace: readonly Row[];
  readonly airspace_remarks: string;
  readonly environment: readonly Row[];
  readonly environment_remarks: string;
  readonly recon: readonly Row[];
  readonly recon_remarks: string;
  readonly drape: readonly Row[];
  readonly drape_remarks: string;
  readonly additional_risks: string;
  readonly footer_text: string;
}

const printed = (iso: string | null): string => (iso && validDate(iso) ? jspPrintedDate(iso) : '');
const km0 = (km: number): string => String(Math.round(km));
const numText = (n: number | null): string => (n === null ? '' : String(n));
const kg = (n: number | null): string => (n === null ? '' : `${one(n)} kg`);
const litres = (n: number | null): string => (n === null ? '' : `${one(n)} L`);

/** Build the PDF's manifest from a saved JSA. Nothing is computed by the template. */
export function jsaManifest(input: {
  readonly tenant: JsaManifestTenant;
  readonly project: Pick<Project, 'number' | 'title' | 'client_name'>;
  readonly jsa: JsaDraft;
  /** The plan's survey blocks in project order, with their lines (for the survey specifications). */
  readonly blocks: readonly Pick<
    Block,
    | 'id'
    | 'name'
    | 'terrain_clearance_m'
    | 'trav_spacing_m'
    | 'trav_direction_deg'
    | 'tie_spacing_m'
    | 'tie_direction_deg'
  >[];
  readonly lines: readonly Pick<
    PlannedLine,
    'block_id' | 'kind' | 'length_m' | 'reflight_of_line_id' | 'deleted_at'
  >[];
  /**
   * The revision this PDF will be (the JSA's `pdf_revision` + 1): carried in the manifest and recorded with the PDF,
   * never printed on the document the client reads (R139, MB 2 Nov 2026).
   */
  readonly revision: number;
}): JsaManifest {
  const { tenant, project: p, jsa: j } = input;
  const specs = j.blocks.map((b) => {
    const blk = input.blocks.find((x) => x.id === b.block_id);
    return blk ? jsaBlockSpec(blk, input.lines) : null;
  });
  const totalKm = specs.reduce((s, x) => s + (x ? x.production.km + x.tie.km : 0), 0);
  const details: Row[] = [
    ['Client', p.client_name],
    ['Project', p.title],
    ['Principal contact', j.details.principal_contact],
    ['Contact number', j.details.contact_number],
    ['Base location', j.details.base_location],
    ['Number of blocks', String(j.blocks.length)],
    ['Total line km', km0(totalKm)],
    ['Est start date', j.details.est_start],
    ['Est end date', j.details.est_end],
  ];
  const signer = (n: number, name: string, role: string): JsaManifestSigner => {
    const t = docusealSignatureTags(n);
    return { name, role, signature_tag: t.signature, date_tag: t.date };
  };
  return {
    schema: JSA_MANIFEST_SCHEMA,
    tenant,
    job_number: p.number,
    client: p.client_name,
    project_title: p.title,
    revision: String(input.revision),
    authors: [
      {
        section: 'Part A: Survey Safety Information',
        name: j.part_a_name.trim(),
        date: printed(j.part_a_date),
      },
      { section: 'Part B: Risk Assessment', name: j.part_b_name.trim(), date: printed(j.part_b_date) },
    ],
    crew: j.crew.map((c, i) => signer(i + 1, c.name, c.role)),
    managers: j.managers.map((m, i) => signer(j.crew.length + i + 1, m.name, m.position.trim())),
    details,
    blocks: j.blocks.map((b, i) => {
      const s = specs[i];
      const spec: Row[] = [
        ['Block name', b.name],
        ['Survey type', j.details.survey_type],
        ['Terrain clearance (m, survey height)', numText(s?.survey_height_m ?? null)],
        ['Production lines', ''],
        ['   Direction (°)', numText(s?.production.direction ?? null)],
        ['   Spacing (m)', numText(s?.production.spacing ?? null)],
        [
          '   Average line length (km)',
          s && s.production.count ? km0(s.production.km / s.production.count) : '',
        ],
        ['   Total line length (km)', s ? km0(s.production.km) : ''],
        ['Tie lines', ''],
        ['   Direction (°)', numText(s?.tie.direction ?? null)],
        ['   Spacing (m)', numText(s?.tie.spacing ?? null)],
        ['   Average line length (km)', s && s.tie.count ? km0(s.tie.km / s.tie.count) : ''],
        ['   Total line length (km)', s ? km0(s.tie.km) : ''],
        ['Block total line length (km)', s ? km0(s.production.km + s.tie.km) : ''],
        ['Survey ground mode', b.ground_mode],
      ];
      return {
        title: `Survey block: ${b.name}`,
        spec,
        picture: `block-${b.block_id}`,
        picture_caption: `Block: ${b.name}`,
      };
    }),
    operating: JSA_OPERATING.map((f) => [f.label, (j.operating[f.key] ?? '').trim()] as const),
    operating_remarks: (j.operating.remarks ?? '').trim(),
    terrain: j.blocks.map((b, i) => {
      const s = specs[i];
      const g = pct(b.gradient);
      const su = pct(b.surface);
      return {
        title: `Survey block: ${b.name}`,
        gradient: [
          ...JSA_GRADIENT.map((f) => [f.label, numText(b.gradient[f.key])] as const),
          ['Total', g.any ? numText(g.total) : ''] as const,
        ],
        surface: [
          ...JSA_SURFACE.map((f) => [f.label, numText(b.surface[f.key])] as const),
          ['Total', su.any ? numText(su.total) : ''] as const,
        ],
        heights: [
          ['Tree height (m)', numText(b.tree_height_m)],
          ['Canopy height (m)', numText(b.canopy_height_m)],
          ['Survey height (m)', numText(s?.survey_height_m ?? null)],
        ],
        hazards: JSA_HAZARDS.map((h) => ({ label: h.label, rating: b.hazards[h.key] ?? '' })),
        remarks: b.remarks.trim(),
      };
    }),
    aircraft: j.aircraft.map((a) => ({
      title: a.registration,
      rows: [
        ['Aircraft type', a.type_name],
        ...JSA_AIRCRAFT_DETAILS.map((f) => [f.label, (a.details[f.key] ?? '').trim()] as const),
      ],
      remarks: a.remarks.trim(),
    })),
    logistics: JSA_LOGISTICS.map((f) => [f.label, (j.logistics[f.key] ?? '').trim()] as const),
    performance: j.aircraft.map((a) => {
      const perf = jsaPerformance(a);
      const pi = a.performance;
      return {
        title: [a.registration, a.type_name].filter((x) => x.trim()).join(', '),
        rows: [
          ['A) Empty weight in survey configuration', '', kg(perf.a_kg)],
          ['B) Crew and all equipment weight (incl. survival gear)', '', kg(perf.b_kg)],
          ['C) Zero fuel weight (A + B)', '', kg(perf.c_kg)],
          ['D) Planned fuel load', litres(perf.d_l), kg(perf.d_kg)],
          ['E) Take-off weight (C + D)', '', kg(perf.e_kg)],
          ['F) MTOW', '', kg(perf.f_kg)],
          [
            'G) One way base to block transit',
            perf.g_km === null ? '' : `${one(perf.g_km)} km`,
            litres(perf.g_l),
          ],
          ['H) Reserve fuel', pi.reserve_min === null ? '' : `${pi.reserve_min} min`, litres(perf.h_l)],
          [`I) Take-off and landing fuel allowance at ${JSA_TAKEOFF_LANDING_MIN} min`, '', litres(perf.i_l)],
          [
            'J) Max time available for survey (D − 2 × G − H − I)',
            '',
            perf.j_h === null ? '' : `${one(perf.j_h)} h`,
          ],
        ],
        survey_time: jsaSurveyTimeWords(perf, pi.fuel_flow_lph),
        over_mtow: perf.over_mtow,
        verified_by: a.verified_by.trim(),
        verified_date: printed(a.verified_date),
      };
    }),
    airspace: JSA_AIRSPACE.map((f) => [f.label, (j.airspace[f.key] ?? '').trim()] as const),
    airspace_remarks: (j.airspace.remarks ?? '').trim(),
    environment: JSA_ENVIRONMENT.map((f) => [f.label, (j.airspace[f.key] ?? '').trim()] as const),
    environment_remarks: (j.airspace.environment_remarks ?? '').trim(),
    recon: JSA_RECON.map((f) => [f.label, (j.recon[f.key] ?? '').trim()] as const),
    recon_remarks: j.recon.remarks.trim(),
    drape: j.recon.drape.map((r) => [r.range.trim(), r.percent.trim()] as const),
    drape_remarks: j.recon.drape_remarks.trim(),
    additional_risks: j.additional_risks.trim(),
    footer_text: `${p.number} ${p.client_name}`.trim(),
  };
}

/** The sign-off date in the file name form (for screens and messages). */
export const jsaFileDate = jspFileDate;
